/**
 * Biomarker Status Colors
 * Single source of truth for biomarker status colors
 * These match the Tailwind config values in src/tailwind.config.js
 */

export const BIOMARKER_STATUS_COLORS = {
    /** Green (#42D4B0) - Value within reference range (Normal/Optimal) */
    NORMAL: '#42D4B0',
    OPTIMAL: '#42D4B0',
    /** Orange (#FFBE7A) - Value below reference range (Low) */
    LOW: '#FFBE7A',
    /** Pink/Red (#F15E87) - Value above reference range (High) */
    HIGH: '#F15E87',
    /** Orange (#FFBE7A) - Borderline values */
    BORDERLINE: '#FFBE7A',
    /** Orange (#FFBE7A) - Elevated values */
    ELEVATED: '#FFBE7A',
    /** Pink/Red (#F15E87) - Positive disease markers (e.g., RF, CA-125) */
    POSITIVE: '#F15E87',
    /** Green (#42D4B0) - Negative disease markers (good result) */
    NEGATIVE: '#42D4B0',
    /** Orange (#FFBE7A) - Vitamin/mineral deficiencies */
    DEFICIENT: '#FFBE7A',
    /** Pink/Red (#F15E87) - Toxicity levels (e.g., high Vitamin D) */
    TOXIC: '#F15E87',
    /** Orange (#FFBE7A) - Impaired function (e.g., low eGFR) */
    IMPAIRED: '#FFBE7A',
} as const;

/**
 * Biomarker Value Color
 * Dark teal color used for displaying biomarker numeric values
 */
export const BIOMARKER_VALUE_COLOR = '#294B5A';

/**
 * Get color for a biomarker status
 * @param status - The biomarker status
 * @returns The hex color string
 */
export const getStatusColor = (
    status:
        | 'Normal'
        | 'Low'
        | 'High'
        | 'Optimal'
        | 'Borderline'
        | 'Elevated'
        | 'Positive'
        | 'Negative'
        | 'Deficient'
        | 'Toxic'
        | 'Impaired'
        | 'Unknown'
): string => {
    switch (status) {
        case 'Normal':
        case 'Optimal':
            return BIOMARKER_STATUS_COLORS.NORMAL;
        case 'Low':
            return BIOMARKER_STATUS_COLORS.LOW;
        case 'High':
            return BIOMARKER_STATUS_COLORS.HIGH;
        case 'Borderline':
            return BIOMARKER_STATUS_COLORS.BORDERLINE;
        case 'Elevated':
            return BIOMARKER_STATUS_COLORS.ELEVATED;
        case 'Positive':
            return BIOMARKER_STATUS_COLORS.POSITIVE;
        case 'Negative':
            return BIOMARKER_STATUS_COLORS.NEGATIVE;
        case 'Deficient':
            return BIOMARKER_STATUS_COLORS.DEFICIENT;
        case 'Toxic':
            return BIOMARKER_STATUS_COLORS.TOXIC;
        case 'Impaired':
            return BIOMARKER_STATUS_COLORS.IMPAIRED;
        default:
            return '#9CA3AF'; // gray-400 for unknown status
    }
};

/**
 * Tailwind CSS class names for biomarker status colors
 * Use these when you need Tailwind utility classes
 */
export const BIOMARKER_STATUS_CLASSES = {
    NORMAL: 'text-mld-status-normal',
    OPTIMAL: 'text-mld-status-optimal',
    LOW: 'text-mld-status-low',
    HIGH: 'text-mld-status-high',
    BORDERLINE: 'text-mld-status-borderline',
    ELEVATED: 'text-mld-status-elevated',
    POSITIVE: 'text-mld-status-positive',
    NEGATIVE: 'text-mld-status-negative',
    DEFICIENT: 'text-mld-status-deficient',
    TOXIC: 'text-mld-status-toxic',
    IMPAIRED: 'text-mld-status-impaired',
    BG_NORMAL: 'bg-mld-status-normal',
    BG_OPTIMAL: 'bg-mld-status-optimal',
    BG_LOW: 'bg-mld-status-low',
    BG_HIGH: 'bg-mld-status-high',
    BG_BORDERLINE: 'bg-mld-status-borderline',
    BG_ELEVATED: 'bg-mld-status-elevated',
    BG_POSITIVE: 'bg-mld-status-positive',
    BG_NEGATIVE: 'bg-mld-status-negative',
    BG_DEFICIENT: 'bg-mld-status-deficient',
    BG_TOXIC: 'bg-mld-status-toxic',
    BG_IMPAIRED: 'bg-mld-status-impaired',
    BORDER_NORMAL: 'border-mld-status-normal',
    BORDER_OPTIMAL: 'border-mld-status-optimal',
    BORDER_LOW: 'border-mld-status-low',
    BORDER_HIGH: 'border-mld-status-high',
    BORDER_BORDERLINE: 'border-mld-status-borderline',
    BORDER_ELEVATED: 'border-mld-status-elevated',
    BORDER_POSITIVE: 'border-mld-status-positive',
    BORDER_NEGATIVE: 'border-mld-status-negative',
    BORDER_DEFICIENT: 'border-mld-status-deficient',
    BORDER_TOXIC: 'border-mld-status-toxic',
    BORDER_IMPAIRED: 'border-mld-status-impaired',
} as const;

/**
 * Get Tailwind class name for a biomarker status
 * @param status - The biomarker status
 * @param variant - The CSS property (text, bg, border)
 * @returns The Tailwind class name
 */
export const getStatusClass = (
    status:
        | 'Normal'
        | 'Low'
        | 'High'
        | 'Optimal'
        | 'Borderline'
        | 'Elevated'
        | 'Positive'
        | 'Negative'
        | 'Deficient'
        | 'Toxic'
        | 'Impaired'
        | 'Unknown',
    variant: 'text' | 'bg' | 'border' = 'text'
): string => {
    const prefix = variant === 'text' ? '' : variant === 'bg' ? 'BG_' : 'BORDER_';
    const key = `${prefix}${status.toUpperCase()}` as keyof typeof BIOMARKER_STATUS_CLASSES;

    return BIOMARKER_STATUS_CLASSES[key] || 'text-gray-400';
};
